<template lang="pug">
.c-questions-page
  //- Header Section
  section.c-questions-page__header
    .c-questions-page__header-content
      h1.c-questions-page__heading Questions
      p.c-questions-page__subtitle Manage featured questions and their answers

  //- Content Section
  .c-questions-content
    //- Table Section
    section.c-questions-table-section
      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--question
            .c-skeleton-cell.c-skeleton-cell--date
      
      //- Table
      template(v-else-if="!apiError")
        el-table(
          v-if="items.items.length > 0"
          :data="items.items"
          @row-click="$router.push(`questions/${$event.id}`)"
          style="width: 100%"
        ).clickable-rows
          el-table-column(width="15")
          
          el-table-column(prop="content" label="Question" min-width="300")
            template(#default="scope")
              .lh-sm
                .font-black {{ scope.row.content }}
          
          el-table-column(prop="createdAt" label="Created Date" min-width="200")
            template(#default="scope")
              .lh-sm
                .font-medium {{ formatDate(scope.row.createdAt) }}

          el-table-column(width="15")

        //- Empty State
        .c-questions-empty(v-else)
          .c-questions-empty__icon
            fai(type="fal" icon="question-circle").size-48
          h3.c-questions-empty__title No questions found
          p.c-questions-empty__text No featured questions have been created yet.

      //- Pagination
      .c-questions-pagination(v-if="items.items.length > 0 && !loading && !apiError")
        .c-questions-pagination__controls
          el-button.c-questions-pagination__button(
            @click="handleCurrentChange(currentPage - 1)"
            :disabled="currentPage === 1"
          ) Prev
          .c-questions-pagination__info
            span.c-questions-pagination__page-number Page {{ currentPage }}
            span.c-questions-pagination__item-count Showing {{ startItem }}-{{ endItem }}
          el-button.c-questions-pagination__button(
            @click="handleCurrentChange(currentPage + 1)"
            :disabled="!hasMorePages"
          ) Next

  //- Error State
  .c-questions-error(v-if="apiError && !loading")
    .c-questions-error__icon
      fai(type="fal" icon="exclamation-triangle").size-48
    h3.c-questions-error__title Error loading questions
    p.c-questions-error__text {{ apiError }}
    el-button.c-questions-error__button(
      @click="getItems"
      type="primary"
    ) Retry
</template>

<script lang="ts">
import {
  reactive, onMounted, defineComponent, ref, computed,
} from 'vue';
import { ElMessage } from 'element-plus';
import shared from '../shared';
import { findAllFeaturedQuestions } from '@/graphs/gql';

export default defineComponent({
  name: 'ManageContent_Media_Questions',
  setup() {
    const { makeGraphRequest, getAuthToken, loading } = shared();
    
    // Initialize loading as true to show skeleton immediately
    loading.value = true;

    const items = reactive({ items: [] });
    const apiError = ref(null);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const hasMorePages = ref(true);

    // Computed properties for pagination display
    const startItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + 1;
    });

    const endItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + items.items.length;
    });

    async function getItems() {
      try {
        loading.value = true;
        apiError.value = null;

        const skip = (currentPage.value - 1) * pageSize.value;
        const response = await makeGraphRequest('query', findAllFeaturedQuestions, {
          take: pageSize.value,
          skip,
        }, getAuthToken());

        const fetchedItems = response.data.findAllFeaturedQuestions;
        // Handle edge case: if we got no results and we're not on page 1
        if (fetchedItems.length === 0 && currentPage.value > 1) {
          // Go back to the previous page
          currentPage.value -= 1;
          hasMorePages.value = false; // We know there are no more pages

          // Show user feedback
          ElMessage.info(`Reached end of results. Showing page ${currentPage.value}.`);

          // Recursively call getItems to fetch the previous page
          await getItems();
          return;
        }

        items.items = fetchedItems;

        // Check if we received a full page of results
        // If we got less than pageSize items, there are no more pages
        hasMorePages.value = fetchedItems.length === pageSize.value;

        // Additional safety: if we got exactly 0 results on page 1, no more pages
        if (fetchedItems.length === 0 && currentPage.value === 1) {
          hasMorePages.value = false;
        }
      } catch (err) {
        console.error('Error fetching questions:', err);
        apiError.value = err.message || 'Failed to fetch questions';
      } finally {
        loading.value = false;
      }
    }

    function formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    function handleCurrentChange(page) {
      if (page < 1 || (page > currentPage.value && !hasMorePages.value)) {
        return;
      }

      // Prevent going to the same page
      if (page === currentPage.value) {
        return;
      }

      currentPage.value = page;
      getItems();
    }

    onMounted(async () => {
      await getItems();
    });

    return {
      items,
      loading,
      apiError,
      currentPage,
      pageSize,
      hasMorePages,
      startItem,
      endItem,
      getItems,
      formatDate,
      handleCurrentChange,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-questions-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }
}

.c-questions-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

// Table Section
.c-questions-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  overflow: hidden;

  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;

    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Pagination - Simple prev/next style
.c-questions-pagination {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
  border-top: 1px solid $gray-100;

  &__controls {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  &__button {
    border-radius: 0.375rem;
    font-weight: 500;
    transition: all 0.15s ease;
    background-color: transparent;
    border: 1px solid $gray-300;
    color: $gray-700;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;

    &:hover:not(:disabled) {
      background-color: transparent;
      border-color: $gray-500;
      color: $gray-900;
    }

    &:disabled {
      border-color: $gray-300;
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    min-width: 140px;
  }

  &__page-number {
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-900;
  }

  &__item-count {
    font-size: 0.75rem;
    color: $gray-600;
  }

  @media (max-width: 640px) {
    &__controls {
      flex-direction: column;
      gap: 1rem;
      width: 100%;
    }

    &__button {
      width: 100%;
    }

    &__info {
      order: -1;
    }
  }
}

// Font utilities
.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

// Skeleton Loader
.c-skeleton-loader {
  width: 100%;
  padding: 1rem 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &--question {
    flex: 1;
    min-width: 250px;
  }

  &--date {
    width: 180px;
    flex-shrink: 0;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Empty State
.c-questions-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;

  &__icon {
    color: $gray-400;
    margin-bottom: 1rem;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0 0 0.5rem 0;
  }

  &__text {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
  }
}

// Error State
.c-questions-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;

  &__icon {
    color: #f56c6c;
    margin-bottom: 1rem;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0 0 0.5rem 0;
  }

  &__text {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0 0 1.5rem 0;
  }

  &__button {
    background-color: #f56c6c !important;
    border-color: #f56c6c !important;
    color: white !important;

    &:hover {
      background-color: #e45656 !important;
      border-color: #e45656 !important;
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .c-questions-page {
    padding: 1.5rem;

    &__heading {
      font-size: 1.5rem;
    }
  }
}
</style>
